/**
 * Bundle flavour mix picker.
 * Added Sep 2026 for Vince. Not part of stock Venue.
 *
 * A facade over the Hulk/YMQ options app. Reads each YMQ option group, renders
 * one quantity row per flavour instead of one dropdown per bag, and writes the
 * choice back into YMQ's radios. The app keeps doing validation and building
 * line-item properties, so nothing downstream of add-to-cart changes.
 */
class MixPicker extends HTMLElement {
  connectedCallback() {
    // connectedCallback fires again on every re-attach. relocate() moves this
    // element next to YMQ's own boxes, and YMQ re-renders that container when
    // a radio changes — so without this guard, build() re-runs, clicks more
    // radios, triggers another re-render, and the page locks up.
    if (this.built) return;
    this.built = true;

    const cfgEl = this.querySelector('[data-mix-config]');
    if (!cfgEl) return;
    try {
      this.config = JSON.parse(cfgEl.textContent);
    } catch (e) {
      return; // malformed metafield: leave the stock YMQ UI alone
    }
    if (!Array.isArray(this.config) || !this.config.length) return;

    // YMQ renders after us, so wait for its groups before building anything.
    this.waitFor(() => document.querySelectorAll('.ymq-options-box').length > 0, 8000)
      .then(() => this.build())
      .catch(() => {}); // app missing or slow: stock UI stays, nothing breaks
  }

  waitFor(test, timeout) {
    return new Promise((resolve, reject) => {
      if (test()) return resolve();
      const started = Date.now();
      const timer = setInterval(() => {
        if (test()) { clearInterval(timer); resolve(); }
        else if (Date.now() - started > timeout) { clearInterval(timer); reject(); }
      }, 120);
    });
  }

  /** Every YMQ group whose label starts with `match`, in numeric order. */
  groupsFor(match) {
    return [...document.querySelectorAll('.ymq-options-box')]
      .map((box) => {
        const label = box.querySelector('.ymq_lable, .ymq-option-title');
        return { box, text: label ? label.textContent.trim() : '' };
      })
      .filter((g) => g.text.toLowerCase().startsWith(match.toLowerCase()))
      .sort((a, b) => (parseInt(a.text.replace(/\D+/g, ''), 10) || 0)
                    - (parseInt(b.text.replace(/\D+/g, ''), 10) || 0));
  }

  build() {
    this.sections = [];

    this.config.forEach((cfg) => {
      const groups = this.groupsFor(cfg.match);
      if (!groups.length) return;

      // flavours come from the app, so renaming one there updates this picker
      const flavours = [...groups[0].box.querySelectorAll('input[type="radio"]')].map((input) => {
        const wrap = input.closest('label, .ymq-option-value, div') || input.parentElement;
        const img = wrap ? wrap.querySelector('img') : null;
        return { value: input.value, img: img ? img.src : null };
      });
      if (flavours.length < 2) return;

      const counts = flavours.map((_, i) => 0);
      // even split, remainder to the first flavour, so the page starts valid
      const base = Math.floor(groups.length / flavours.length);
      counts.forEach((_, i) => { counts[i] = base; });
      counts[0] += groups.length - base * flavours.length;

      this.sections.push({ cfg, groups, flavours, counts, cap: groups.length });
    });

    if (!this.sections.length) return;

    this.render();
    this.relocate();
    this.hideYmq();
    this.hidden = false;
    this.sections.forEach((s) => this.writeBack(s));
  }

  render() {
    const html = this.sections.map((s, si) => `
      <div class="mix-picker__section">
        <div class="mix-picker__head">
          <p class="mix-picker__title">${this.esc(s.cfg.label || 'Choose your mix')}</p>
          <span class="mix-picker__chip" data-chip="${si}"></span>
        </div>
        ${s.flavours.map((f, fi) => `
          <div class="mix-picker__row">
            ${f.img ? `<img class="mix-picker__img" src="${this.esc(f.img)}" alt="">`
                    : `<span class="mix-picker__img"></span>`}
            <span class="mix-picker__text">
              <span class="mix-picker__name">${this.esc(f.value)}</span>
              ${s.cfg.sub ? `<span class="mix-picker__sub">${this.esc(s.cfg.sub)}</span>` : ''}
            </span>
            <span class="mix-picker__step">
              <button type="button" data-s="${si}" data-f="${fi}" data-d="-1"
                      aria-label="One fewer ${this.esc(f.value)}">&minus;</button>
              <output data-out="${si}-${fi}" aria-live="polite">0</output>
              <button type="button" data-s="${si}" data-f="${fi}" data-d="1"
                      aria-label="One more ${this.esc(f.value)}">+</button>
            </span>
          </div>`).join('')}
        <div class="mix-picker__shortcuts" data-short="${si}">
          <button type="button" class="mix-picker__link" data-even="${si}">Even split</button>
          ${s.flavours.map((f, fi) =>
            `<button type="button" class="mix-picker__link" data-all="${si}-${fi}">All ${this.esc(this.shortName(f.value, s.flavours, s.cfg))}</button>`
          ).join('')}
        </div>
      </div>`).join('');

    this.insertAdjacentHTML('beforeend', `<div class="mix-picker__inner">${html}</div>`);

    this.addEventListener('click', (e) => {
      const step = e.target.closest('[data-d]');
      if (step) return this.step(+step.dataset.s, +step.dataset.f, +step.dataset.d);
      const even = e.target.closest('[data-even]');
      if (even) return this.preset(+even.dataset.even, 'even');
      const all = e.target.closest('[data-all]');
      if (all) {
        const [si, fi] = all.dataset.all.split('-').map(Number);
        return this.preset(si, fi);
      }
    });

    this.paint();
  }

  /**
   * Label for the "All ..." shortcut buttons.
   *
   * Takes the first word that is neither a generic descriptor nor shared with
   * another flavour. "Veggie Ragu with Pasta" gives "Ragu", not "Veggie",
   * because "Veggie" describes the whole range rather than this flavour.
   * A section can override any of them with a "short" map in its config:
   *   {"match":"Single Bag", "short":{"Veggie Ragu with Pasta":"Ragu"}}
   */
  shortName(value, all, cfg) {
    if (cfg && cfg.short && cfg.short[value]) return cfg.short[value];

    const GENERIC = ['veggie', 'vince', 'the', 'a', 'of', 'with', 'and',
                     'style', 'pack', 'bag', 'single', 'large', 'new', 'our'];
    const norm = (w) => w.toLowerCase().replace(/[^a-z]/g, '').replace(/s$/, '');
    const others = (all || [])
      .filter((f) => f.value !== value)
      .flatMap((f) => f.value.split(/\s+/).map(norm));
    const words = value.split(/\s+/).filter((w) => /[a-z]/i.test(w));

    const pick = words.find((w) => {
      const n = norm(w);
      return n && GENERIC.indexOf(n) === -1 && others.indexOf(n) === -1;
    }) || words.find((w) => norm(w) && GENERIC.indexOf(norm(w)) === -1)
      || words[0] || value;

    return pick.replace(/[^A-Za-z-]/g, '');
  }

  esc(s) {
    return String(s).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  step(si, fi, d) {
    const s = this.sections[si];
    const total = s.counts.reduce((a, b) => a + b, 0);
    if (d < 0 && s.counts[fi] === 0) return;
    if (d > 0 && total >= s.cap) return;
    s.counts[fi] += d;
    this.paint();
    this.writeBack(s);
  }

  preset(si, mode) {
    const s = this.sections[si];
    if (mode === 'even') {
      const base = Math.floor(s.cap / s.flavours.length);
      s.counts = s.flavours.map(() => base);
      s.counts[0] += s.cap - base * s.flavours.length;
    } else {
      s.counts = s.flavours.map((_, i) => (i === mode ? s.cap : 0));
    }
    this.paint();
    this.writeBack(s);
  }

  paint() {
    this.sections.forEach((s, si) => {
      const total = s.counts.reduce((a, b) => a + b, 0);
      s.counts.forEach((n, fi) => {
        const out = this.querySelector(`[data-out="${si}-${fi}"]`);
        if (out) out.textContent = n;
      });
      const chip = this.querySelector(`[data-chip="${si}"]`);
      if (chip) {
        chip.textContent = `${total} of ${s.cap}`;
        chip.className = 'mix-picker__chip ' +
          (total === s.cap ? 'is-done' : 'is-todo');
      }
      this.querySelectorAll(`[data-s="${si}"]`).forEach((b) => {
        const d = +b.dataset.d, fi = +b.dataset.f;
        b.disabled = d < 0 ? s.counts[fi] === 0 : total >= s.cap;
      });
    });
  }

  /**
   * Push the chosen quantities into YMQ's radios: the first N groups take
   * flavour 0, the next M take flavour 1, and so on. YMQ then validates and
   * builds the properties exactly as it always has.
   */
  writeBack(s) {
    let gi = 0;
    s.counts.forEach((n, fi) => {
      for (let i = 0; i < n && gi < s.groups.length; i++, gi++) {
        const radios = [...s.groups[gi].box.querySelectorAll('input[type="radio"]')];
        const target = radios.find((r) => r.value === s.flavours[fi].value);
        if (target && !target.checked) target.click();
      }
    });
  }

  /**
   * Sit where the YMQ block sat, so the buy box order is unchanged.
   * Runs once. YMQ owns the nodes around it and re-renders them on change,
   * so re-entering this would detach and re-attach the picker.
   */
  relocate() {
    if (this.relocated) return;
    this.relocated = true;
    const first = document.querySelector('.ymq-options-box');
    if (first && first.parentElement) first.parentElement.insertBefore(this, first);
  }

  hideYmq() {
    document.documentElement.classList.add('mix-picker-active');
  }
}

if (!customElements.get('mix-picker')) customElements.define('mix-picker', MixPicker);
